import { Outlet, createFileRoute } from '@tanstack/react-router'
import { Suspense, useState } from 'react'

import { Box, HStack } from '@masterplandev/ui'

import { LoadingIndicator } from '@/core/components/LoadingIndicator'
import { Tab, TabList, Tabs } from '@/core/components/StaticTabs'
import {
  ResizableDrawer,
  ResizableDrawerCloseButton,
  ResizableDrawerContent,
  ResizableDrawerContentFixed,
  ResizableDrawerHeader,
  ResizableDrawerHeading,
} from '@/core/components/ui/ResizableDrawer/ResizableDrawer'
import useTranslation from '@/core/hooks/useTranslation'
import { RouterLink } from '@/router/RouterLink'
import { UserGroupDetails } from '@/user-groups/components/UserGroupDetails/UserGroupDetails'
import { UserGroupsActionsMenu } from '@/user-groups/components/UserGroupsActions/UserGroupsActionsMenu'
import { useUserGroup } from '@/user-groups/queries/useUserGroup'

export const Route = createFileRoute('/company/_management/groups/$groupId')({
  component: RouteComponent,
})

function RouteComponent() {
  const { translate } = useTranslation('user-groups')
  const { groupId } = Route.useParams()
  const navigate = Route.useNavigate()
  const { data: userGroup } = useUserGroup({ userGroupId: groupId })
  const [isOpen, setIsOpen] = useState(true)

  const handleClose = () => {
    navigate({
      to: '/company/groups',
      search: (previous) => previous,
    })
  }

  return (
    <ResizableDrawer
      isOpen={isOpen}
      onClose={handleClose}
      data-testid="user-groups-details-drawer">
      <ResizableDrawerHeader>
        <ResizableDrawerHeading
          wordBreak="break-word"
          noOfLines={2}
          data-testid="user-groups-table-user-group-details-name">
          {userGroup?.name}
        </ResizableDrawerHeading>
        <HStack>
          <UserGroupsActionsMenu
            groupId={groupId}
            isPrivate={userGroup?.is_private}
            size="md"
            variant="ghost"
            colorScheme="mpGraphite"
            aria-label="Menu"
          />
          <ResizableDrawerCloseButton
            aria-label="Close"
            onClick={() => setIsOpen(false)}
            data-testid="user-groups-details-drawer-close-button"
          />
        </HStack>
      </ResizableDrawerHeader>

      <Suspense fallback={<LoadingIndicator />}>
        <Box px={6}>
          <UserGroupDetails groupId={groupId} />
        </Box>

        <ResizableDrawerContentFixed top={16}>
          <Tabs>
            <TabList pl={6} sx={{ '&& > *': { h: 10, pt: 0, pb: 2 } }}>
              <Tab
                data-testid="user-groups-details-tab"
                as={RouterLink}
                to="/company/groups/$groupId/details"
                search={(previous) => ({ ...previous })}>
                {translate('tabs.details')}
              </Tab>
              <Tab
                data-testid="user-groups-members-tab"
                as={RouterLink}
                to="/company/groups/$groupId/members"
                search={(previous) => ({ ...previous })}>
                {translate('tabs.members')}
              </Tab>
              <Tab
                data-testid="user-groups-learnpaths-tab"
                as={RouterLink}
                to="/company/groups/$groupId/learnpaths"
                search={(previous) => ({ ...previous })}>
                {translate('tabs.learnpaths')}
              </Tab>
            </TabList>
          </Tabs>
        </ResizableDrawerContentFixed>

        <ResizableDrawerContent
          flex="1"
          display="flex"
          flexDirection="column"
          background="mp2.neutralLight.150">
          <Outlet />
        </ResizableDrawerContent>
      </Suspense>
    </ResizableDrawer>
  )
}
